import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Calendar, Tag, ArrowRight, Filter } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';

// Curated main tags to show in the filter bar
const mainTags = ['Custom Development', 'Performance', 'AI', 'Strategy', 'Support', 'Security'];
const allTags = Array.from(new Set(blogPosts.flatMap(post => post.tags)));

export default function Blog() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [showAllTags, setShowAllTags] = useState(false);

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTag = !selectedTag || post.tags.includes(selectedTag);
    return matchesSearch && matchesTag;
  });

  return (
    <div className="min-h-screen pt-16">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-night-900 via-night-800 to-night-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-launch-blue via-launch-purple to-launch-orange bg-clip-text text-transparent">Blog</span>
            </h1>
            <p className="text-xl text-offwhite/80 max-w-3xl mx-auto">
              Technical insights and engineering notes from our team
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-12 bg-night-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-offwhite/40" size={20} />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-lg bg-night-700 text-offwhite border border-night-600 focus:border-launch-blue focus:outline-none transition-colors"
              />
            </div>
            
            {/* Tag Filter */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Filter size={18} className="text-offwhite/60" />
                <span className="text-sm font-medium text-offwhite/80">Filter by topic:</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setSelectedTag('')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    !selectedTag
                      ? 'bg-gradient-to-r from-launch-blue to-launch-purple text-white shadow-lg shadow-launch-purple/20'
                      : 'bg-night-700 text-offwhite/80 hover:bg-night-600'
                  }`}
                >
                  All Posts
                </button>
                {mainTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setSelectedTag(tag)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      selectedTag === tag
                        ? 'bg-gradient-to-r from-launch-blue to-launch-purple text-white shadow-lg shadow-launch-purple/20'
                        : 'bg-night-700 text-offwhite/80 hover:bg-night-600'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
                <button
                  onClick={() => setShowAllTags(!showAllTags)}
                  className="px-4 py-2 rounded-lg font-medium bg-night-700 text-offwhite/80 hover:bg-night-600 transition-all flex items-center gap-2"
                >
                  <Tag size={16} />
                  {showAllTags ? 'Show Less' : 'More Topics'}
                </button>
              </div>
              
              {/* Additional Tags Dropdown */}
              {showAllTags && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3 flex gap-2 flex-wrap"
                >
                  {allTags
                    .filter(tag => !mainTags.includes(tag))
                    .map((tag) => (
                      <button
                        key={tag}
                        onClick={() => setSelectedTag(tag)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          selectedTag === tag
                            ? 'bg-gradient-to-r from-launch-blue to-launch-purple text-white shadow-lg shadow-launch-purple/20'
                            : 'bg-night-600 text-offwhite/70 hover:bg-night-500'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-24 bg-night-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.08,
                },
              },
            }}
          >
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                className="bg-night-700 rounded-xl overflow-hidden cursor-pointer group h-full"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { 
                    opacity: 1, 
                    y: 0,
                    transition: {
                      type: "spring",
                      stiffness: 100,
                    },
                  },
                }}
                whileHover={{ 
                  y: -5,
                  boxShadow: "0 15px 40px rgba(168, 85, 247, 0.2)",
                }}
              >
                <Link to={`/blog/${post.slug}`} className="block h-full">
                  <div className="p-6 h-full flex flex-col">
                    <div className="flex items-center gap-4 text-sm text-offwhite/60 mb-4">
                      <span className="flex items-center">
                        <Calendar size={16} className="mr-1" />
                        {new Date(post.date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </span>
                      <span>•</span>
                      <span>{post.readTime}</span>
                    </div>
                    
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-launch-blue transition-colors">
                      {post.title}
                    </h3>
                    
                    <p className="text-offwhite/70 mb-4 flex-grow">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="text-xs font-mono px-2 py-1 rounded bg-night-600 text-launch-blue"
                        >
                          #{tag}
                        </span>
                      ))}
                      {post.tags.length > 3 && (
                        <span className="text-xs font-mono px-2 py-1 rounded bg-night-600 text-offwhite/60">
                          +{post.tags.length - 3}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-night-600">
                      <span className="text-sm text-offwhite/60">By {post.author}</span>
                      <span className="text-launch-blue group-hover:text-launch-purple transition-colors inline-flex items-center">
                        Read More
                        <ArrowRight className="ml-1" size={16} />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </motion.div>
          
          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-offwhite/60">No articles found matching your search.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
